/**
 * ⚠️ INTEGRATION NOTE — READ BEFORE DROPPING THIS IN ⚠️
 *
 * Every converted screen imports RouteNames from a relative `constants`
 * barrel (e.g. `from '../../constants'` or `from '../../../constants'`),
 * the same barrel that already exports FormFields, CategoryValues, etc. for
 * those screens. That means this file should NOT be dropped in as a
 * standalone module — its contents need to be merged into your existing
 * `src/constants/index.js` (or wherever that barrel actually lives), e.g.:
 *
 *   // src/constants/index.js
 *   export { RouteNames } from './RouteNames';   // this file
 *   export { FormFields } from './FormFields';   // already exists
 *   export { CategoryValues } from './CategoryValues'; // already exists
 *   ...
 *
 * If your constants barrel already has a RouteNames export with different
 * values (likely, since the business/admin side of App.js uses literal path
 * strings like '/admin/points' rather than named constants), reconcile the
 * two rather than blindly overwriting — check for naming collisions first.
 */

/**
 * Route path constants for the customer-facing side of the app (as opposed
 * to the existing business/admin routes already defined inline in App.js).
 *
 * Every value here is referenced by name (RouteNames.X) throughout the
 * converted customer screens (SignIn, SignUp, Coupons, Businesses, Home,
 * MyAccount, Payment, etc.) — this file is the single source of truth those
 * screens were written against.
 *
 * Dynamic segments use the `:id` convention and are paired with a `*Path`
 * helper for building a concrete link (e.g. `RouteNames.couponsDetailsPath(42)`)
 * since several screens build hrefs/navigate calls with a template literal
 * (`` `${RouteNames.COUPONS_DETAILS}/${id}` ``) — the constant itself is the
 * route's base path (no leading param), and routing.jsx appends `/:id`.
 */

export const RouteNames = {
  // ─── Auth / Signup ──────────────────────────────────────────────────────
  SIGN_IN: '/customer-login',
  SIGN_UP: '/customer-signup',
  SIGN_UP_EMAIL_PASSWORD_INFO: '/customer-signup/details',
  SIGN_UP_CONTACT_INFO: '/customer-signup/contact-info',
  SIGN_UP_PREFERENCES: '/customer-signup/preferences',
  SIGN_UP_REFERRAL_CODE: '/customer-signup/referral-code',
  SIGN_UP_PRIVACY_POLICY: '/customer-signup/privacy-policy',
  FORGOT_PASSWORD: '/customer-forgot-password',

  // ─── Payment / Post-signup ──────────────────────────────────────────────
  SUCCESSFUL_SIGN_UP: '/Home',
  PAYMENT_FORM: '/customer-payment',
  FREE_PREMIUM: '/customer-payment/free-premium',
  REFERRAL_PROGRAM_FORM: '/customer-payment/referral-program',

  // ─── Home / core nav ────────────────────────────────────────────────────
  HOME: '/home',
  GUEST_HOME: '/',
  LOCATION_SEARCH: '/location-search',

  // ─── Coupons ─────────────────────────────────────────────────────────────
  COUPONS_LISTS_ALL: '/coupons-list',
  COUPONS_MAPS_ALL: '/coupons-map',
  COUPONS_DETAILS: '/coupons-detail',
  COUPONS_APPLY: '/coupons-apply',
  COUPONS_RESERVED: '/coupons-reserved',
  FAVOURITES: '/favourites',
  CATEGORY_SEARCH: '/category-search',
  ONBOARDING: '/onboarding',

  // ─── Businesses ──────────────────────────────────────────────────────────
  BUSINESSES_DETAILS: '/business-detail',
  AFFILIATE_BUSINESS_DETAILS: '/online-brand-detail',
  BUSINESSES_LIST_ALL: '/businesses-list',
  AFFILIATE_BUSINESSES_LIST_ALL: '/online-brands-list',
  BUSINESSES_LIST_SEARCH: '/businesses-search',
  BUSINESSES_LIST_RECOMMENDED: '/businesses-recommended',
  BUSINESSES_FOLLOWED: '/businesses-followed',

    // ─── Blog ──────────────────────────────────────────────────────────
  BLOGS: '/blogs',
  BLOG_DETAILS: '/blogs/:id',

  // ─── Account ─────────────────────────────────────────────────────────────
  ACCOUNT: '/my-account',
  PREFERENCES: '/preferences',
  DELETE_ACCOUNT: '/my-account/delete',

  // ─── Used coupons / referrals ────────────────────────────────────────────
  USED_COUPONS_HISTORY: '/used-coupons/history',
  USED_COUPONS_RESERVED: '/used-coupons/reserved',
  REFERRALS_BUSINESSES: '/referrals/businesses',
  REFERRALS_CUSTOMERS: '/referrals/customers',

  // ─── Legal ───────────────────────────────────────────────────────────────
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/customer-terms-of-service',
};

export default RouteNames;